import Paragraph from '@tiptap/extension-paragraph';
import { mergeAttributes } from '@tiptap/core';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { selectionInsideBlockquote } from '../lib/pathstone_blockquote_content.js';
import { POST_EDITOR_PARAGRAPH_STYLES } from '../lib/post_editor_paragraph_style.js';
import {
  POST_EDITOR_PARAGRAPH_STYLE_CLASSES,
  POST_EDITOR_PARAGRAPH_ACCENT_IDS,
  POST_EDITOR_PARAGRAPH_ACCENT_CLASSES,
  POST_EDITOR_PARAGRAPH_TAG_BY_STYLE,
} from '../lib/post_editor_constants.js';
import {
  marksToStripForHeadingSubheading,
  removeMarksFromFragment,
  stripMarksInRange,
} from '../lib/post_editor_inline_marks.js';

function styleFromElement(element) {
  const d = element.getAttribute('data-ps-post-text-style');
  if (d && POST_EDITOR_PARAGRAPH_STYLES.includes(d)) return d;
  for (const [key, cls] of Object.entries(POST_EDITOR_PARAGRAPH_STYLE_CLASSES)) {
    if (element.classList.contains(cls)) return key;
  }
  // Fallback: derive from tag name. This catches:
  //   - new saves where headings are <h2>/<h3>
  //   - pasted content (e.g. from Google Docs / Word) that uses raw <hN>
  //     tags without any of our class/data-attribute markers
  const tag = element.tagName?.toLowerCase();
  if (tag === 'h2') return 'heading';
  if (tag === 'h3') return 'subheading';
  return null;
}

function accentFromElement(element) {
  const d = element.getAttribute('data-ps-post-text-accent');
  if (d && POST_EDITOR_PARAGRAPH_ACCENT_IDS.includes(d)) return d;
  for (const id of POST_EDITOR_PARAGRAPH_ACCENT_IDS) {
    const cls = POST_EDITOR_PARAGRAPH_ACCENT_CLASSES[id];
    if (cls && element.classList.contains(cls)) return id;
  }
  return null;
}

function findParagraphDepth($pos) {
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).type.name === 'paragraph') return depth;
  }
  return -1;
}

/**
 * When the selection is a `NodeSelection` wrapping a paragraph (e.g. the drag
 * handle was clicked), `state.selection.$from.depth` is 0 (doc level) and
 * `findParagraphDepth` returns -1. Resolve the paragraph node/position
 * directly so callers like the drag context menu can apply a style.
 */
function resolveParagraphFromSelection(state) {
  const { selection } = state;
  if (selection instanceof NodeSelection && selection.node?.type?.name === 'paragraph') {
    return { paragraphNode: selection.node, paragraphPos: selection.from };
  }
  const paragraphDepth = findParagraphDepth(selection.$from);
  if (paragraphDepth < 0) return null;
  return {
    paragraphNode: selection.$from.node(paragraphDepth),
    paragraphPos: selection.$from.before(paragraphDepth),
  };
}

function setSelectedTextAsParagraphStyle({ state, dispatch, style, accent, marksToStrip = [] }) {
  const { selection, schema } = state;
  if (selection.empty) return false;

  const { $from, $to } = selection;
  if (!$from.sameParent($to)) return false;

  const parent = $from.parent;
  if (parent.type.name !== 'paragraph') return false;

  const paragraphDepth = $from.depth;
  const paragraphStart = $from.before(paragraphDepth);
  const paragraphEnd = $from.after(paragraphDepth);
  const before = parent.content.cut(0, $from.parentOffset);
  const selected = removeMarksFromFragment(
    parent.content.cut($from.parentOffset, $to.parentOffset),
    marksToStrip.length ? marksToStrip : [schema.marks.fontFamilyClass].filter(Boolean)
  );
  const after = parent.content.cut($to.parentOffset, parent.content.size);

  const styledAttrs = {
    ...parent.attrs,
    postTextStyle: style,
    postTextAccent: accent,
  };
  const nodes = [];
  if (before.size) nodes.push(parent.type.create(parent.attrs, before));

  const styledNodeIndex = nodes.length;
  nodes.push(parent.type.create(styledAttrs, selected));

  if (after.size) nodes.push(parent.type.create(parent.attrs, after));

  if (!dispatch) return true;

  const tr = state.tr.replaceWith(paragraphStart, paragraphEnd, nodes);
  const styledNodeStart = nodes
    .slice(0, styledNodeIndex)
    .reduce((pos, node) => pos + node.nodeSize, paragraphStart);
  const styledNode = nodes[styledNodeIndex];
  const selectionStart = styledNodeStart + 1;
  const selectionEnd = Math.max(selectionStart, styledNodeStart + styledNode.nodeSize - 1);

  tr.setSelection(TextSelection.create(tr.doc, selectionStart, selectionEnd));
  dispatch(tr.scrollIntoView());
  return true;
}

export const PostParagraphStyleExtension = Paragraph.extend({
  name: 'paragraph',

  addAttributes() {
    return {
      postTextStyle: {
        default: null,
        parseHTML: (element) => styleFromElement(element),
        renderHTML: (attributes) => {
          if (!attributes.postTextStyle) return {};
          return { 'data-ps-post-text-style': attributes.postTextStyle };
        },
      },
      postTextAccent: {
        default: null,
        parseHTML: (element) => accentFromElement(element),
        renderHTML: (attributes) => {
          if (!attributes.postTextAccent) return {};
          return { 'data-ps-post-text-accent': attributes.postTextAccent };
        },
      },
      psQuoteRole: {
        default: null,
        parseHTML: (element) => {
          const role = element.getAttribute('data-ps-quote-role');
          return role === 'quote' || role === 'attribution' ? role : null;
        },
        renderHTML: (attributes) => {
          if (!attributes.psQuoteRole) return {};
          return { 'data-ps-quote-role': attributes.psQuoteRole };
        },
      },
      psRowRole: {
        default: null,
        parseHTML: (element) => {
          const role = element.getAttribute('data-ps-row-role');
          return role === 'title' || role === 'subtitle' ? role : null;
        },
        renderHTML: (attributes) => {
          if (!attributes.psRowRole) return {};
          if (attributes.psRowRole === 'subtitle') {
            return {
              'data-ps-row-role': 'subtitle',
              class: 'ps-resource-row__subtitle',
            };
          }
          return { 'data-ps-row-role': attributes.psRowRole };
        },
      },
      psRowGap: {
        default: false,
        parseHTML: (element) => element.hasAttribute('data-ps-row-gap'),
        renderHTML: (attributes) => {
          if (!attributes.psRowGap) return {};
          return { 'data-ps-row-gap': '' };
        },
      },
    };
  },

  // Match <p>, <h2>, and <h3> at parse time so we can absorb existing post
  // content (which serialised as <p class="ps-post-style-heading">), freshly
  // saved content (which serialises as <h2>/<h3>), and pasted hN tags.
  // StarterKit's built-in Heading extension is disabled in
  // TiptapEditorComponent so it doesn't compete for the same tags.
  parseHTML() {
    return [
      { tag: 'p' },
      { tag: 'h2' },
      { tag: 'h3' },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { postTextStyle, postTextAccent } = node.attrs;
    const tag = POST_EDITOR_PARAGRAPH_TAG_BY_STYLE[postTextStyle] || 'p';
    const classes = [];
    if (postTextStyle && POST_EDITOR_PARAGRAPH_STYLE_CLASSES[postTextStyle]) {
      classes.push(POST_EDITOR_PARAGRAPH_STYLE_CLASSES[postTextStyle]);
    }
    if (postTextStyle === 'heading' || postTextStyle === 'subheading') {
      const acc = POST_EDITOR_PARAGRAPH_ACCENT_IDS.includes(postTextAccent) ? postTextAccent : 'black';
      classes.push(POST_EDITOR_PARAGRAPH_ACCENT_CLASSES[acc] || POST_EDITOR_PARAGRAPH_ACCENT_CLASSES.black);
    }
    const mergedClass = [HTMLAttributes.class, ...classes].filter(Boolean).join(' ');
    return [tag, mergeAttributes(HTMLAttributes, { class: mergedClass || undefined }), 0];
  },

  addCommands() {
    return {
      setPostParagraphStyle:
        ({ style, accent }) =>
        ({ state, dispatch, chain }) => {
          if (selectionInsideBlockquote(state)) return false;

          const isHeadingStyle = style === 'heading' || style === 'subheading';
          const isBodyStyle = style === 'body';
          const isCaptionStyle = style === 'caption';
          const acc = isHeadingStyle
            ? POST_EDITOR_PARAGRAPH_ACCENT_IDS.includes(accent)
              ? accent
              : 'black'
            : null;
          const marksToStrip = isHeadingStyle
            ? marksToStripForHeadingSubheading(state.schema)
            : isBodyStyle || isCaptionStyle
              ? [state.schema.marks.psTextColor].filter(Boolean)
            : [];

          if (
            setSelectedTextAsParagraphStyle({
              state,
              dispatch,
              style,
              accent: acc,
              marksToStrip,
            })
          ) {
            return true;
          }

          const resolved = resolveParagraphFromSelection(state);
          if (!resolved) return false;

          const { paragraphNode, paragraphPos } = resolved;
          const contentFrom = paragraphPos + 1;
          const contentTo = paragraphPos + paragraphNode.nodeSize - 1;

          if (!dispatch) return true;

          let tr = state.tr.setNodeMarkup(paragraphPos, undefined, {
            ...paragraphNode.attrs,
            postTextStyle: style,
            postTextAccent: acc,
          });

          if (contentTo > contentFrom) {
            const marks = isHeadingStyle || isBodyStyle || isCaptionStyle
              ? marksToStrip
              : [state.schema.marks.fontFamilyClass].filter(Boolean);
            tr = stripMarksInRange(tr, contentFrom, contentTo, marks);
          }

          dispatch(tr.scrollIntoView());
          return true;
        },
    };
  },
});
